import { DataTable, type LocalDataColumn } from "@nymbl/nos2-rules";
import { useMemo } from "react";
import { MetricInfo } from "@/components/incentives/MetricInfo";
import { INCENTIVE_HELP } from "@/lib/column-help";
import { formatAwardPercent, goalRange } from "@/lib/incentives/incentives";
import { withCells } from "@/lib/table-columns";
import type { IncentiveGoal } from "@/schemas/incentives";

/** A stored fraction as a percent number, so filters take "50" for 50%. Display only. */
const asPercent = (v: number | null) => (v === null ? null : v * 100);

export const GOAL_COLUMNS: LocalDataColumn<IncentiveGoal>[] = [
  {
    key: "goal",
    label: "Goal",
    type: "text",
    accessor: (g) => g.goal,
    searchText: (g) => g.level ?? "",
    help: INCENTIVE_HELP.goal,
  },
  {
    key: "weight",
    label: "Weight",
    type: "number",
    accessor: (g) => asPercent(g.weight),
    help: INCENTIVE_HELP.weight,
  },
  {
    key: "range",
    label: "Min/target/max",
    type: "text",
    accessor: (g) => goalRange(g),
    align: "right",
    help: INCENTIVE_HELP.range,
  },
  {
    key: "actual",
    label: "Actual",
    type: "text",
    accessor: (g) => g.actual,
    align: "right",
    help: INCENTIVE_HELP.actual,
  },
  {
    key: "projected",
    label: "Projected",
    type: "text",
    accessor: (g) => g.projected,
    align: "right",
    help: INCENTIVE_HELP.projected,
  },
  {
    key: "achieved",
    label: "Achieved",
    type: "number",
    accessor: (g) => asPercent(g.achieved),
    help: INCENTIVE_HELP.achieved,
  },
  {
    key: "awarded",
    label: "Awarded",
    type: "number",
    accessor: (g) => asPercent(g.awarded),
    help: INCENTIVE_HELP.awarded,
  },
];

const COLUMNS = withCells(GOAL_COLUMNS, {
  goal: {
    className: "min-w-40 md:min-w-0",
    render: (g) => (
      <>
        <div className="flex items-center gap-1">
          <span className="font-semibold">{g.goal}</span>
          <MetricInfo goal={g} />
        </div>
        {g.level && <div className="text-xs text-muted-foreground">{g.level}</div>}
      </>
    ),
  },
  weight: { render: (g) => formatAwardPercent(g.weight) },
  range: { className: "max-w-36" },
  actual: { render: (g) => g.actual ?? "—" },
  projected: { render: (g) => g.projected ?? "—" },
  achieved: { render: (g) => formatAwardPercent(g.achieved) },
  awarded: {
    render: (g) => <span className="font-semibold">{formatAwardPercent(g.awarded)}</span>,
  },
});

const byGoal = (a: IncentiveGoal, b: IncentiveGoal) =>
  a.goal.localeCompare(b.goal) || a.id.localeCompare(b.id);

interface GoalsTableProps {
  title: string;
  goals: IncentiveGoal[];
  year: number;
}

/** One goals table (Company or Personal), weight first as Incentyv lists them. The caller hides it when there are no goals. */
export function GoalsTable({ title, goals, year }: GoalsTableProps) {
  const rows = useMemo(() => [...goals].sort(byGoal), [goals]);
  return (
    <DataTable
      label={`${title}, ${year}`}
      title={title}
      scope={title}
      search={{ placeholder: `Search ${title.toLowerCase()}…` }}
      columns={COLUMNS}
      rows={rows}
      rowKey={(g) => g.id}
      defaultSort="weight"
      defaultSortDir="desc"
    />
  );
}
